/**
 * Freedom Funding Readiness™ Digital Academy & Resource Hub
 * ─────────────────────────────────────────────────────────
 * Landing page at /funding-readiness. Anchors the whole platform brand
 * (dark forest + gold). Kept modular: <Hero>, <RoadmapOptIn>, <MasterclassReplay>,
 * <ShopPreview>, <Advisory>, <NonprofitBuildLab>, <CloseCta>.
 */
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, Download, ChevronRight, CheckCircle2, Sparkles, Crown,
  Compass, Users, Award, Wrench, ShieldCheck, Menu, X, FileText,
} from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Palette
const FFR = {
  forest: "#0B3D2E",
  forestDeep: "#062218",
  forest2: "#0E5138",
  gold: "#C9A75A",
  goldSoft: "#F5EEDA",
  ivory: "#FAF7EE",
  parchment: "#F1ECDE",
};

/* ---------- Navigation ---------- */
function FfrNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const location = useLocation();
  const onFundingPage = location.pathname === "/funding-readiness";
  const items = [
    { href: "#top",         label: "Home" },
    { href: "#roadmap",     label: "Funding Readiness" },
    { toShop: true,         label: "Resource Shop" },
    { href: "#masterclass", label: "Masterclasses" },
    { href: "#build-lab",   label: "AI Build Lab" },
    { toLibrary: true,      label: "My Library" },
    { href: "#work-with-me", label: "Work With Me", accent: true },
    { href: "#contact",     label: "Contact" },
  ];
  const scrollTo = (href) => {
    if (href === "#top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <nav
      className="fixed top-0 inset-x-0 z-40 transition-all"
      style={{
        background: scrolled ? "rgba(6,34,24,0.92)" : "rgba(11,61,46,0.72)",
        backdropFilter: "blur(14px)",
        borderBottom: scrolled ? `1px solid ${FFR.gold}40` : `1px solid ${FFR.gold}22`,
      }}
      data-testid="ffr-nav"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/funding-readiness" className="flex items-center gap-2.5" data-testid="ffr-logo">
          <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: FFR.gold }}>
            <Crown size={17} style={{ color: FFR.forestDeep }} strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Freedom Funding Readiness™</div>
            <div className="text-white text-[13px] font-black tracking-[-0.01em] leading-none mt-0.5">Digital Academy</div>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-6">
          {items.map((i) => {
            if (i.toShop) {
              return (
                <Link key={i.label} to="/shop" className="text-[13px] font-semibold text-white/85 hover:text-white transition-colors">
                  {i.label}
                </Link>
              );
            }
            if (i.toLibrary) {
              return (
                <Link key={i.label} to="/library" data-testid="ffr-nav-my-library" className="text-[13px] font-semibold text-white/85 hover:text-white transition-colors">
                  {i.label}
                </Link>
              );
            }
            if (i.accent) {
              if (!onFundingPage) {
                return (
                  <Link
                    key={i.label}
                    to={`/funding-readiness${i.href}`}
                    data-testid={`ffr-nav-${i.label.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-[12px] font-bold uppercase tracking-[0.06em] rounded-full h-9 px-4 flex items-center transition-opacity hover:opacity-90"
                    style={{ background: FFR.gold, color: FFR.forestDeep }}
                  >
                    {i.label}
                  </Link>
                );
              }
              return (
                <button
                  key={i.label}
                  onClick={() => scrollTo(i.href)}
                  data-testid={`ffr-nav-${i.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="text-[12px] font-bold uppercase tracking-[0.06em] rounded-full h-9 px-4 transition-opacity hover:opacity-90"
                  style={{ background: FFR.gold, color: FFR.forestDeep }}
                >
                  {i.label}
                </button>
              );
            }
            if (!onFundingPage) {
              return (
                <Link
                  key={i.label}
                  to={`/funding-readiness${i.href}`}
                  data-testid={`ffr-nav-${i.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="text-[13px] font-semibold text-white/85 hover:text-white transition-colors"
                >
                  {i.label}
                </Link>
              );
            }
            return (
              <button
                key={i.label}
                onClick={() => scrollTo(i.href)}
                data-testid={`ffr-nav-${i.label.toLowerCase().replace(/\s/g, '-')}`}
                className="text-[13px] font-semibold text-white/85 hover:text-white transition-colors"
              >
                {i.label}
              </button>
            );
          })}
        </div>
        <button className="lg:hidden text-white" onClick={() => setOpen((s) => !s)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden px-6 pb-4" style={{ background: "rgba(6,34,24,0.98)" }}>
          {items.map((i) => {
            if (i.toShop) {
              return (
                <Link key={i.label} to="/shop" onClick={() => setOpen(false)} className="block py-2.5 text-[14px] font-semibold text-white/85">
                  {i.label}
                </Link>
              );
            }
            if (i.toLibrary) {
              return (
                <Link key={i.label} to="/library" onClick={() => setOpen(false)} className="block py-2.5 text-[14px] font-semibold text-white/85">
                  {i.label}
                </Link>
              );
            }
            if (!onFundingPage) {
              return (
                <Link
                  key={i.label}
                  to={`/funding-readiness${i.href}`}
                  onClick={() => setOpen(false)}
                  className="block w-full text-left py-2.5 text-[14px] font-semibold text-white/85"
                >
                  {i.label}
                </Link>
              );
            }
            return (
              <button
                key={i.label}
                onClick={() => { scrollTo(i.href); setOpen(false); }}
                className="block w-full text-left py-2.5 text-[14px] font-semibold text-white/85"
              >
                {i.label}
              </button>
            );
          })}
        </div>
      )}
    </nav>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section
      className="relative pt-32 lg:pt-40 pb-24 lg:pb-32 overflow-hidden"
      data-testid="ffr-hero"
      style={{ background: `linear-gradient(180deg, ${FFR.forestDeep} 0%, ${FFR.forest} 100%)` }}
    >
      {/* Decorative gold rings */}
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-10 pointer-events-none" style={{ border: `2px solid ${FFR.gold}` }} />
      <div className="absolute -top-20 -right-20 w-[440px] h-[440px] rounded-full opacity-15 pointer-events-none" style={{ border: `2px solid ${FFR.gold}` }} />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-8 pointer-events-none blur-3xl" style={{ background: FFR.gold }} />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-7 border"
            style={{ background: "rgba(201,167,90,0.10)", borderColor: `${FFR.gold}55` }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: FFR.gold }} />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: FFR.gold }}>
              For Nonprofit Leaders
            </span>
          </div>

          <div className="mb-4">
            <div className="font-black uppercase tracking-[-0.02em] leading-none" style={{ fontSize: "clamp(20px, 2.2vw, 28px)", color: FFR.gold, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Beyond the Grant™
            </div>
          </div>
          <h1
            data-testid="ffr-hero-headline"
            className="font-black uppercase leading-[0.94] tracking-[-0.02em] text-white"
            style={{ fontSize: "clamp(38px, 6.5vw, 84px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}
          >
            Build a nonprofit{" "}
            <span style={{ color: FFR.gold }}>funders can trust.</span>
          </h1>

          <p className="mt-7 text-[16px] sm:text-[18px] leading-[1.65] text-white/80 max-w-2xl">
            Become funding ready with proven frameworks, templates, AI-powered systems, and coaching from a nonprofit executive, grant reviewer, and grant recipient.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a href="#roadmap" data-testid="ffr-cta-roadmap">
              <Button className="h-14 px-7 rounded-full font-bold uppercase text-[12px] tracking-[0.06em] hover:opacity-95 transition-opacity" style={{ background: FFR.gold, color: FFR.forestDeep, boxShadow: `0 18px 40px -12px ${FFR.gold}80` }}>
                <Download size={15} className="mr-2" />
                Download free roadmap
              </Button>
            </a>
            <Link to="/shop" data-testid="ffr-cta-shop">
              <Button variant="outline" className="h-14 px-6 rounded-full font-bold uppercase text-[12px] tracking-[0.06em] bg-transparent text-white hover:bg-white/10 border-2" style={{ borderColor: "rgba(255,255,255,0.55)" }}>
                Browse resources
              </Button>
            </Link>
          </div>
        </div>

        {/* Right — pillar of trust card */}
        <div className="lg:col-span-5">
          <div className="relative">
            <div
              className="rounded-3xl p-8 sm:p-10 border relative overflow-hidden"
              style={{ background: `linear-gradient(140deg, ${FFR.forest2} 0%, ${FFR.forestDeep} 100%)`, borderColor: `${FFR.gold}44` }}
            >
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-15 blur-3xl" style={{ background: FFR.gold }} />
              <div className="relative">
                <Crown size={28} style={{ color: FFR.gold }} className="mb-5" />
                <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-2" style={{ color: FFR.gold }}>What you get</div>
                <ul className="space-y-3 mt-4">
                  {[
                    "Grant Readiness Roadmap™ (free)",
                    "7 focused Toolkits ($27 each)",
                    "Operations flagship ($97)",
                    "Advisory membership ($99/mo)",
                    "AI Build Lab for Nonprofits",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-white text-[14.5px]">
                      <CheckCircle2 size={16} style={{ color: FFR.gold }} className="flex-shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Grant Readiness Roadmap opt-in ---------- */
function RoadmapOptIn() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", organization: "", website: "", mission_area: "", annual_budget: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);  // { download_url, email_sent, ... }
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const backend = process.env.REACT_APP_BACKEND_URL;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please share your first name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${backend}/api/funding-readiness/roadmap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error");
      const data = await res.json();
      setResult(data);
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch {
      toast.error("Something went wrong. Please email info@itsthefreedominme.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="roadmap" className="py-24 lg:py-32" data-testid="ffr-roadmap-optin" style={{ background: FFR.ivory }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 border" style={{ borderColor: `${FFR.gold}55`, background: FFR.goldSoft }}>
            <span className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.forest }}>Free Resource</span>
          </div>
          <h2 className="font-black uppercase leading-[1.02] tracking-[-0.025em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
            Grant Readiness{" "}
            <span style={{ color: FFR.gold }}>Roadmap™</span>
          </h2>
          <p className="mt-5 text-[16px] leading-[1.65]" style={{ color: FFR.forestDeep }}>
            The exact framework a nonprofit executive, grant reviewer, and grant recipient uses to score, prepare, and win funding — condensed into one downloadable roadmap.
          </p>
          <ul className="mt-6 space-y-2.5 text-[14px]" style={{ color: FFR.forestDeep }}>
            {["The 7 stages of funding readiness", "What reviewers score first", "Common mistakes to avoid", "Where to start today"].map((t) => (
              <li key={t} className="flex items-center gap-2.5">
                <ChevronRight size={15} style={{ color: FFR.gold }} />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl p-8 lg:p-10 shadow-xl border-2" style={{ background: "white", borderColor: `${FFR.gold}55` }}>
            {done ? (
              <div className="text-center py-8" data-testid="ffr-roadmap-success">
                <div className="w-14 h-14 rounded-full mx-auto grid place-items-center mb-4" style={{ background: FFR.goldSoft }}>
                  <CheckCircle2 size={28} style={{ color: FFR.forest }} />
                </div>
                <h3 className="font-black text-[22px] uppercase tracking-[-0.02em]" style={{ color: FFR.forest }}>You&rsquo;re in</h3>
                <p className="mt-3 text-[14.5px]" style={{ color: FFR.forestDeep }}>
                  Your Grant Readiness Roadmap™ (PDF) is ready. Tap the button below to download it right now — we&apos;ve also sent a copy to your inbox.
                </p>
                {result?.download_url && (
                  <a
                    href={result.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-black uppercase tracking-[0.14em] text-[13px]"
                    style={{ background: FFR.gold, color: FFR.forestDeep }}
                    data-testid="ffr-roadmap-download-btn"
                  >
                    Download the Roadmap (PDF)
                    <ChevronRight size={14} />
                  </a>
                )}
                {result && !result.email_sent && (
                  <p className="mt-4 text-[12px]" style={{ color: `${FFR.forestDeep}99` }}>
                    Note: we couldn&apos;t send the copy to your inbox this time — please use the button above to grab your PDF.
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="ffr-roadmap-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <FieldInput label="First name*" value={form.first_name} onChange={(v) => upd("first_name", v)} testid="ffr-fn" />
                  <FieldInput label="Last name" value={form.last_name} onChange={(v) => upd("last_name", v)} testid="ffr-ln" />
                </div>
                <FieldInput label="Email*" type="email" value={form.email} onChange={(v) => upd("email", v)} testid="ffr-email" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <FieldInput label="Organization" value={form.organization} onChange={(v) => upd("organization", v)} testid="ffr-org" />
                  <FieldInput label="Website" value={form.website} onChange={(v) => upd("website", v)} testid="ffr-website" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <FieldInput label="Mission area" value={form.mission_area} onChange={(v) => upd("mission_area", v)} testid="ffr-mission" />
                  <FieldInput label="Annual budget" value={form.annual_budget} onChange={(v) => upd("annual_budget", v)} testid="ffr-budget" placeholder="e.g. $500K – $1M" />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="ffr-roadmap-submit"
                  className="w-full h-13 rounded-full mt-2 font-bold uppercase text-[12px] tracking-[0.06em] hover:opacity-95 transition-opacity disabled:opacity-70"
                  style={{ height: "54px", background: FFR.forest, color: "white" }}
                >
                  {submitting ? "Sending..." : "Send me the roadmap"}
                  {!submitting && <ArrowRight size={15} className="ml-2" />}
                </Button>
                <p className="text-[11px] text-center mt-2" style={{ color: `${FFR.forestDeep}99` }}>
                  We&rsquo;ll deliver your roadmap instantly on the next screen and also email a copy. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldInput({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase font-bold tracking-[0.18em] mb-1.5 block" style={{ color: FFR.forestDeep }}>
        {label}
      </label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="rounded-lg border-2 h-11"
        style={{ borderColor: `${FFR.forest}22` }}
      />
    </div>
  );
}

/* ---------- Masterclass replay card ---------- */
function MasterclassReplay() {
  const sc = useSiteContent();
  const stripeUrl = (sc.ffr_stripe_replay || "").trim();
  const available = !!stripeUrl;
  return (
    <section id="masterclass" className="py-24 lg:py-32" data-testid="ffr-masterclass" style={{ background: FFR.parchment }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Masterclass</div>
          <h2 className="font-black uppercase leading-[1.02] tracking-[-0.02em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
            Beyond the Grant™ <span style={{ color: FFR.gold }}>Replay</span>
          </h2>
          <p className="mt-5 text-[17px] leading-[1.65]" style={{ color: FFR.forestDeep }}>
            What funders <em>actually</em> look for — the anatomy of a fundable nonprofit, from a grant reviewer who reads hundreds of applications a year.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 rounded-3xl p-8 lg:p-10 border-2" style={{ background: "white", borderColor: `${FFR.gold}44` }}>
            <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Includes with replay</div>
            <ul className="mt-5 space-y-3 text-[15px]" style={{ color: FFR.forestDeep }}>
              {[
                { icon: FileText, t: "Full masterclass replay (YouTube-powered stream)" },
                { icon: Compass,  t: "Grant Readiness Roadmap™ (PDF)" },
                { icon: ShieldCheck, t: "Individual License — non-transferable" },
              ].map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <r.icon size={18} style={{ color: FFR.gold }} className="mt-0.5" />
                  <span>{r.t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden shadow-xl" style={{ background: `linear-gradient(140deg, ${FFR.forest} 0%, ${FFR.forestDeep} 100%)` }}>
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-20 blur-3xl" style={{ background: FFR.gold }} />
              <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Replay price</div>
              <div className="font-black text-[64px] leading-none mt-3" style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>$10</div>
              <div className="text-[13px] text-white/70 mt-2">One-time · individual license</div>

              <PurchaseButton stripeUrl={stripeUrl} available={available} testid="ffr-replay-purchase" label="Purchase Replay" productName="Beyond the Grant Replay" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- License-gated purchase button ---------- */
function PurchaseButton({ stripeUrl, available, testid, label = "Purchase", productName = "resource" }) {
  const [showLicense, setShowLicense] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const openCheckout = () => {
    if (!accepted) {
      toast.error("Please accept the license agreement to continue.");
      return;
    }
    window.open(stripeUrl, "_blank", "noopener,noreferrer");
    setShowLicense(false);
    setAccepted(false);
  };

  if (!available) {
    return (
      <button
        type="button"
        disabled
        data-testid={testid}
        className="mt-8 w-full h-13 rounded-full font-bold uppercase text-[12px] tracking-[0.06em] opacity-60 cursor-not-allowed"
        style={{ background: "rgba(255,255,255,0.15)", color: FFR.goldSoft, height: "54px" }}
      >
        Coming Soon — check back
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowLicense(true)}
        data-testid={testid}
        className="mt-8 w-full rounded-full font-bold uppercase text-[12px] tracking-[0.06em] hover:opacity-95 transition-opacity"
        style={{ background: FFR.gold, color: FFR.forestDeep, height: "54px" }}
      >
        {label} <ArrowRight size={15} className="inline ml-1" />
      </button>

      {showLicense && (
        <LicenseModal
          productName={productName}
          accepted={accepted}
          onAccept={setAccepted}
          onCancel={() => { setShowLicense(false); setAccepted(false); }}
          onContinue={openCheckout}
        />
      )}
    </>
  );
}

/* ---------- License Agreement Modal ---------- */
function LicenseModal({ productName, accepted, onAccept, onCancel, onContinue }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(6,34,24,0.85)", backdropFilter: "blur(8px)" }}
      data-testid="ffr-license-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-7 py-5 border-b flex items-center justify-between" style={{ borderColor: `${FFR.forest}22` }}>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>License Agreement</div>
            <div className="font-black text-[18px] mt-0.5" style={{ color: FFR.forest }}>{productName}</div>
          </div>
          <button type="button" onClick={onCancel} className="text-[color:var(--muted)]" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="px-7 py-6 overflow-y-auto flex-1 text-[14px]" style={{ color: FFR.forestDeep }}>
          <p className="font-bold mb-3">© Freedom In Me, LLC — All Rights Reserved.</p>
          <p className="mb-4">This purchase grants <strong>one individual, non-transferable license</strong>. Organization, board, and workshop licenses are available separately.</p>
          <p className="font-bold mb-2">The purchaser may NOT:</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] mb-4 list-disc pl-5">
            <li>Share login credentials</li>
            <li>Share replay links</li>
            <li>Download &amp; redistribute</li>
            <li>Upload to YouTube</li>
            <li>Upload to social media</li>
            <li>Use for board training</li>
            <li>Use for staff training</li>
            <li>Use for organizational training</li>
            <li>Resell templates</li>
            <li>Copy templates</li>
            <li>Modify frameworks</li>
            <li>Teach this material</li>
            <li>Create derivative works</li>
            <li>Use in paid consulting</li>
          </ul>
          <p className="text-[13px] italic">Organization, Board, and Workshop licenses are available on request — email info@itsthefreedominme.com.</p>
        </div>

        <div className="px-7 py-5 border-t space-y-4" style={{ borderColor: `${FFR.forest}22`, background: FFR.ivory }}>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="ffr-license-accept-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => onAccept(e.target.checked)}
              data-testid="ffr-license-accept"
              className="mt-1 w-4 h-4 accent-current"
              style={{ accentColor: FFR.forest }}
            />
            <span className="text-[13px]" style={{ color: FFR.forestDeep }}>
              I have read and accept the terms of this <strong>individual, non-transferable license</strong>.
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="button" onClick={onCancel} className="flex-1 h-11 rounded-full font-bold text-[13px] uppercase tracking-[0.05em] border-2 transition-colors" style={{ borderColor: `${FFR.forest}44`, color: FFR.forest }} data-testid="ffr-license-cancel">
              Cancel
            </button>
            <button
              type="button"
              onClick={onContinue}
              disabled={!accepted}
              data-testid="ffr-license-continue"
              className="flex-1 h-11 rounded-full font-bold text-[13px] uppercase tracking-[0.05em] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: FFR.forest, color: "white" }}
            >
              Continue to checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Shop preview ---------- */
function ShopPreview() {
  return (
    <section className="py-24 lg:py-32" data-testid="ffr-shop-preview" style={{ background: FFR.forest, color: "white" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Resource Shop</div>
          <h2 className="font-black uppercase leading-[1.02] tracking-[-0.02em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
            Toolkits, frameworks, and dashboards <span style={{ color: FFR.gold }}>built by an executive.</span>
          </h2>
          <p className="mt-5 text-[16px] leading-[1.65] text-white/75">
            Seven focused toolkits at $27, an operations flagship at $97, and value-driven bundles from $67 to $197. Each purchase includes an individual license and comes with a receipt.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-8 text-[13px] text-white/70">
          {["Funding Readiness", "Evidence Bank", "Program Blueprint", "Board Governance", "Grant Success", "Grant Problem Statement", "Fundraising", "Operations", "3 Bundles"].map((t) => (
            <span key={t} className="px-3 py-1 rounded-full border" style={{ borderColor: `${FFR.gold}44` }}>{t}</span>
          ))}
        </div>

        <Link to="/shop" data-testid="ffr-shop-cta">
          <Button className="h-14 px-7 rounded-full font-bold uppercase text-[12px] tracking-[0.06em]" style={{ background: FFR.gold, color: FFR.forestDeep, boxShadow: `0 18px 40px -12px ${FFR.gold}80` }}>
            Browse the full shop
            <ArrowRight size={15} className="ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

/* ---------- Advisory ---------- */
function Advisory() {
  const sc = useSiteContent();
  const stripeUrl = (sc.ffr_stripe_advisory || "").trim();
  const available = !!stripeUrl;
  const plusUrl = (sc.ffr_stripe_advisory_plus || "").trim();
  const plusAvailable = !!plusUrl;
  return (
    <section id="work-with-me" className="py-24 lg:py-32" data-testid="ffr-advisory" style={{ background: FFR.ivory }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Work With Me</div>
          <h2 className="font-black uppercase leading-[1.02] tracking-[-0.02em]" style={{ fontSize: "clamp(32px, 4.8vw, 60px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
            Two ways to work with Donja — <span style={{ color: FFR.gold }}>directly</span>.
          </h2>
          <p className="mt-4 text-[16px] leading-[1.65]" style={{ color: FFR.forestDeep }}>
            Beyond the toolkits, you can join the monthly Group Session or bring Donja in for custom organizational consulting.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div id="group-session" className="lg:col-span-7 rounded-3xl p-8 lg:p-10 shadow-md border-2 scroll-mt-24" style={{ background: "white", borderColor: `${FFR.gold}44` }}>
            <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Group Session</div>
            <h3 className="font-black uppercase mt-2 leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(24px, 3vw, 36px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
              Freedom Funding <span style={{ color: FFR.gold }}>Readiness™</span>
            </h3>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-black text-[46px] leading-none" style={{ color: FFR.forest }}>$99</span>
              <span className="text-[15px] font-semibold" style={{ color: FFR.forestDeep }}>/ month</span>
            </div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-[13.5px]" style={{ color: FFR.forestDeep }}>
              {[
                "Monthly group coaching call",
                "Monthly office hours",
                "Quarterly funding readiness review",
                "Monthly curated grant opportunities",
                "Resource Library access",
                "Discounts on workshops",
                "Priority enrollment for AI Build Lab",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <CheckCircle2 size={14} style={{ color: FFR.gold }} className="mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="rounded-3xl p-8 lg:p-10 text-white shadow-xl relative overflow-hidden" style={{ background: `linear-gradient(140deg, ${FFR.forest} 0%, ${FFR.forestDeep} 100%)` }}>
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-20 blur-3xl" style={{ background: FFR.gold }} />
              <Crown size={30} style={{ color: FFR.gold }} className="mb-4" />
              <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Join today · Group tier</div>
              <div className="font-black text-[22px] uppercase mt-2 leading-tight" style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
                Become funding ready with monthly guidance.
              </div>
              <PurchaseButton stripeUrl={stripeUrl} available={available} testid="ffr-advisory-purchase" label="Join Group Session" productName="Freedom Funding Readiness™ Advisory — Group" />
            </div>

            <div className="rounded-3xl p-8 lg:p-10 shadow-md border-2 relative" style={{ background: "white", borderColor: FFR.gold }} data-testid="ffr-advisory-plus-card">
              <div className="absolute -top-3 left-6 z-10">
                <div className="text-[10px] uppercase font-black tracking-[0.22em] px-3 py-1.5 rounded shadow-lg" style={{ background: FFR.gold, color: FFR.forestDeep }}>Upgrade · 1:1</div>
              </div>
              <div className="pt-3 text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: FFR.gold }}>Group + Private 1:1</div>
              <h3 className="font-black uppercase mt-2 leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(20px, 2.2vw, 26px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
                Everything in Group, plus one <span style={{ color: FFR.gold }}>private 1:1 with Donja</span> each month.
              </h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-black text-[38px] leading-none" style={{ color: FFR.forest }}>$149</span>
                <span className="text-[13px] font-semibold" style={{ color: FFR.forestDeep }}>/ month</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-[13.5px]" style={{ color: FFR.forestDeep }}>
                <li className="flex items-start gap-2.5"><CheckCircle2 size={14} style={{ color: FFR.gold }} className="mt-0.5 flex-shrink-0" /><span>Everything in the Group Session tier</span></li>
                <li className="flex items-start gap-2.5"><CheckCircle2 size={14} style={{ color: FFR.gold }} className="mt-0.5 flex-shrink-0" /><span>One private 1:1 strategy call with Donja every month</span></li>
                <li className="flex items-start gap-2.5"><CheckCircle2 size={14} style={{ color: FFR.gold }} className="mt-0.5 flex-shrink-0" /><span>Direct feedback on your funding roadmap</span></li>
              </ul>
              <div className="mt-5">
                <PurchaseButton stripeUrl={plusUrl} available={plusAvailable} testid="ffr-advisory-plus-purchase" label="Upgrade to 1:1" productName="Freedom Funding Readiness™ Advisory — Group + 1:1" />
              </div>
              <div className="mt-5 pt-4 border-t" style={{ borderColor: `${FFR.gold}22` }}>
                <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-1.5" style={{ color: FFR.gold }}>Or custom consulting</div>
                <a href="mailto:info@itsthefreedominme.com?subject=Consulting inquiry" data-testid="ffr-consulting-cta" className="text-[13px] font-semibold flex items-center gap-1.5" style={{ color: FFR.forestDeep }}>
                  Email Donja directly
                  <ArrowRight size={13} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- AI Build Lab for Nonprofits ---------- */
function NonprofitBuildLab() {
  const sc = useSiteContent();
  const labUrl = (sc.ffr_stripe_ai_build_lab || "").trim();
  const agentUrl = (sc.ffr_stripe_ai_agent || "").trim();

  const products = [
    {
      testid: "ffr-nplab-1",
      title: "AI Build Lab for Nonprofits",
      subtitle: "Learn to use AI to modernize nonprofit operations.",
      price: "$249",
      priceHint: "Live 2.5-hour session · individual license",
      topics: ["Executive dashboards", "Grant dashboards", "Workflow automation", "Participant intake", "Volunteer management", "Standardized event pages", "Grant reporting", "Board support", "Communications"],
      stripeUrl: labUrl,
      productName: "AI Build Lab for Nonprofits",
    },
    {
      testid: "ffr-nplab-2",
      title: "Build Your Nonprofit AI Agent",
      subtitle: "Prerequisite: completion of AI Build Lab.",
      price: "$249",
      priceHint: "Live session · individual license",
      topics: ["Grant Research agent", "Executive Assistant agent", "Volunteer Coordinator", "Donor Stewardship", "Board Administration", "Event Planning", "Operations", "Program Management"],
      stripeUrl: agentUrl,
      productName: "Build Your Nonprofit AI Agent",
    },
  ];

  return (
    <section id="build-lab" className="py-24 lg:py-32" data-testid="ffr-buildlab" style={{ background: FFR.parchment }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Enrollment Open</div>
          <h2 className="font-black uppercase leading-[1.02] tracking-[-0.02em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
            AI Build Lab <span style={{ color: FFR.gold }}>for Nonprofits.</span>
          </h2>
          <p className="mt-4 text-[16px] leading-[1.65]" style={{ color: FFR.forestDeep }}>
            Two direct-purchase programs. No waitlist. Enroll and start building.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {products.map((p) => {
            const available = !!p.stripeUrl;
            return (
              <div key={p.testid} className="rounded-3xl p-8 lg:p-10 border-2 relative overflow-hidden bg-white flex flex-col" style={{ borderColor: `${FFR.gold}44` }} data-testid={p.testid}>
                <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: FFR.gold }} />
                <Wrench size={26} style={{ color: FFR.forest }} className="mb-4 mt-2" />
                <h3 className="font-black text-[24px] leading-tight tracking-[-0.02em]" style={{ color: FFR.forest }}>{p.title}</h3>
                <p className="mt-2 text-[14.5px]" style={{ color: FFR.forestDeep }}>{p.subtitle}</p>
                <div className="mt-4 flex items-baseline gap-2.5">
                  <span className="font-black text-[36px] leading-none" style={{ color: FFR.forestDeep, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>{p.price}</span>
                  <span className="text-[12px]" style={{ color: `${FFR.forestDeep}99` }}>{p.priceHint}</span>
                </div>
                <ul className="mt-5 space-y-1.5 text-[13.5px] flex-1" style={{ color: FFR.forestDeep }}>
                  {p.topics.map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full" style={{ background: FFR.gold }} />
                      {t}
                    </li>
                  ))}
                </ul>
                <PurchaseButton stripeUrl={p.stripeUrl} available={available} testid={`${p.testid}-cta`} label="Enroll Now" productName={p.productName} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Contact ---------- */
function Contact() {
  return (
    <section id="contact" className="py-20 lg:py-24" data-testid="ffr-contact" style={{ background: FFR.parchment }}>
      <div className="max-w-3xl mx-auto text-center px-6 lg:px-10">
        <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: FFR.gold }}>Contact</div>
        <h2 className="font-black uppercase leading-[1.02] tracking-[-0.02em]" style={{ fontSize: "clamp(28px, 4vw, 48px)", color: FFR.forest, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
          Questions? <span style={{ color: FFR.gold }}>Reach out.</span>
        </h2>
        <p className="mt-5 text-[15px] leading-[1.65]" style={{ color: FFR.forestDeep }}>
          For custom consulting, organization licensing, or press inquiries, email Donja directly.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href="mailto:info@itsthefreedominme.com" data-testid="ffr-contact-email">
            <Button className="h-13 px-6 rounded-full font-bold uppercase text-[12px] tracking-[0.06em] hover:opacity-95 transition-opacity" style={{ background: FFR.forest, color: "white", height: "52px" }}>
              info@itsthefreedominme.com
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------- Close CTA ---------- */
function CloseCta() {
  return (
    <section className="py-28 lg:py-36" data-testid="ffr-close" style={{ background: FFR.forestDeep }}>
      <div className="max-w-3xl mx-auto text-center px-6 lg:px-10">
        <Sparkles size={22} style={{ color: FFR.gold }} className="mx-auto mb-5" />
        <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: FFR.gold }}>Start where you are</div>
        <h2 className="font-black uppercase leading-[1.02] tracking-[-0.025em] text-white" style={{ fontSize: "clamp(32px, 5vw, 56px)", fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
          Every funder trusts a nonprofit <span style={{ color: FFR.gold }}>that&rsquo;s ready.</span>
        </h2>
        <p className="mt-6 text-white/75 text-[16px] leading-[1.65]">
          Take the free Grant Readiness Roadmap, browse the Resource Shop, or join the Advisory — you choose the starting point.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href="#roadmap"><Button className="h-14 px-7 rounded-full font-bold uppercase text-[12px] tracking-[0.06em]" style={{ background: FFR.gold, color: FFR.forestDeep }}>Download the Roadmap</Button></a>
          <Link to="/shop"><Button variant="outline" className="h-14 px-6 rounded-full font-bold uppercase text-[12px] tracking-[0.06em] bg-transparent text-white hover:bg-white/10 border-2" style={{ borderColor: "rgba(255,255,255,0.5)" }}>Browse Shop</Button></Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
function FfrFoot() {
  return (
    <footer className="py-10" style={{ background: FFR.forestDeep, borderTop: `1px solid ${FFR.gold}22` }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-3 text-[12px]" style={{ color: `${FFR.goldSoft}` }}>
        <div>© Freedom In Me, LLC — All Rights Reserved. · Charlotte, North Carolina</div>
        <div className="flex gap-4">
          <Link to="/" className="hover:text-white">AI Build Lab</Link>
          <Link to="/student-camp" className="hover:text-white">Student Camp</Link>
          <a href="mailto:info@itsthefreedominme.com" className="hover:text-white">Contact</a>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Default export ---------- */
export default function FundingReadiness() {
  const location = useLocation();
  useEffect(() => {
    const prev = document.title;
    document.title = "Freedom Funding Readiness™ Digital Academy — Donja Williams";
    return () => { document.title = prev; };
  }, []);
  // Scroll to hash anchor when the page mounts or hash changes (e.g. arriving
  // from /shop or /library via the FfrNav Link).
  useEffect(() => {
    const hash = location.hash || "";
    if (!hash || hash === "#top") {
      if (hash === "#top") window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Give the sections a beat to mount before scrolling.
    const id = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(id);
  }, [location.pathname, location.hash]);
  return (
    <div className="min-h-screen" data-testid="funding-readiness-page" style={{ fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif", background: FFR.ivory }}>
      <FfrNav />
      <main>
        <Hero />
        <RoadmapOptIn />
        <MasterclassReplay />
        <ShopPreview />
        <Advisory />
        <NonprofitBuildLab />
        <Contact />
        <CloseCta />
      </main>
      <FfrFoot />
    </div>
  );
}

// Export sub-components used by /shop
export { FfrNav, FfrFoot, PurchaseButton, FFR };
